/**
 * Read a spooled (> 4 MB) response body back from Rust.
 *
 * `send_http_request` writes a body too large for one WebView2 IPC message to
 * a temp file; `read_file_chunk` hands it back in slices. Each slice arrives as
 * base64 of the RAW bytes (src-tauri/src/response_spool.rs), because decoding
 * a slice on its own turned a multibyte character cut by the slice boundary
 * into two U+FFFD. Text is decoded here, ONCE, with a streaming TextDecoder.
 *
 * A binary body (Rust sets `temp_file_binary`) is not decoded at all: it comes
 * back as `data_base64`, the same shape a small binary body already has, so
 * Preview and Hex work for a large PDF or image too.
 */

/** Must not exceed `MAX_CHUNK_BYTES` in response_spool.rs (base64 → ~2.7 MB per IPC message). */
export const CHUNK_SIZE = 2 * 1024 * 1024;

function base64ToBytes(b64) {
  const bin = atob(b64 || '');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64(bytes) {
  // String.fromCharCode(...hugeArray) overflows the stack; 32 KB at a time.
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

/**
 * @param {(cmd: string, args: object) => Promise<any>} invoke
 * @param {string} path  the `temp_file` Rust returned
 * @param {{ binary?: boolean, isCancelled?: () => boolean }} [opts]  `isCancelled` is asked
 *   before every slice: a request the user cancelled stops reading (the file is still deleted)
 *   instead of pulling hundreds of MB nobody will look at.
 * @returns {Promise<{ text: string } | { base64: string } | { cancelled: true }>}
 */
export async function readTempResponseFile(invoke, path, { binary = false, isCancelled } = {}) {
  const decoder = binary ? null : new TextDecoder('utf-8');
  const textParts = [];
  const byteParts = [];
  let total = 0;
  let offset = 0;
  try {
    for (;;) {
      if (isCancelled?.()) return { cancelled: true };
      const chunk = await invoke('read_file_chunk', { path, offset, length: CHUNK_SIZE });
      const bytes = base64ToBytes(chunk?.data_base64);
      if (binary) {
        byteParts.push(bytes);
        total += bytes.length;
      } else {
        textParts.push(decoder.decode(bytes, { stream: true }));
      }
      // No progress would loop forever; `done` is Rust's word for EOF.
      if (chunk?.done || !(chunk?.next_offset > offset)) break;
      offset = chunk.next_offset;
    }
  } finally {
    // best-effort cleanup — don't block on it
    Promise.resolve()
      .then(() => invoke('delete_temp_file', { path }))
      .catch(() => {});
  }
  if (!binary) {
    textParts.push(decoder.decode());
    return { text: textParts.join('') };
  }
  const all = new Uint8Array(total);
  let at = 0;
  for (const part of byteParts) {
    all.set(part, at);
    at += part.length;
  }
  return { base64: bytesToBase64(all) };
}
